/**
 * Updates a product card's price, savings amount, energy-class badge, and
 * the "Mehr erfahren" link when a size pill on that same card is clicked.
 * Deliberately independent of the theme's built-in variant-picker/product-card
 * reactive system (no VariantSelectedEvent, no section re-fetch, no morph) -
 * everything needed is already embedded as data attributes on each pill at
 * render time, so this is a plain, self-contained DOM update with nothing
 * else on the page to conflict with.
 *
 * Each card actually renders the price block TWICE: once in the visible
 * card face, and once in the theme's hidden "zoom-out list view" markup
 * (toggled by the grid density switcher, inactive by default but always
 * present in the DOM). Updating only the first match - the hidden one -
 * is a real trap here (the theme's own code hits the same issue and picks
 * the second instance for this reason). Updating every match found avoids
 * having to guess which index is the visible one.
 */
document.addEventListener('click', (event) => {
  const pill = event.target.closest('.size-pills__pill');
  if (!pill || pill.disabled) return;

  const card = pill.closest('product-card') || pill.closest('.group-block');
  if (!card) return;

  for (const otherPill of card.querySelectorAll('.size-pills__pill')) {
    otherPill.classList.toggle('size-pills__pill--selected', otherPill === pill);
  }

  const showCompare = pill.dataset.showCompare === 'true';
  const showSave = pill.dataset.showSave === 'true';
  const showPiSheet = pill.dataset.showPiSheet === 'true';
  const energyClass = pill.dataset.energyClass || '';

  for (const priceRow of card.querySelectorAll('[data-card-price-row]')) {
    priceRow.classList.toggle('price-row--sale', showCompare);
  }

  for (const priceEl of card.querySelectorAll('[data-card-price]')) {
    priceEl.textContent = pill.dataset.price || '';
    priceEl.classList.toggle('price--sale', showCompare);
  }

  for (const compareWrapper of card.querySelectorAll('[data-card-compare-wrapper]')) {
    compareWrapper.hidden = !showCompare;
  }

  for (const comparePriceEl of card.querySelectorAll('[data-card-compare-price]')) {
    if (!showCompare) continue;
    const uvpSuffix = comparePriceEl.querySelector('.compare-at-price__uvp');
    comparePriceEl.textContent = pill.dataset.comparePrice || '';
    if (uvpSuffix) comparePriceEl.appendChild(uvpSuffix);
  }

  for (const saveEl of card.querySelectorAll('[data-card-save]')) {
    saveEl.hidden = !showSave;
  }

  for (const saveAmountEl of card.querySelectorAll('[data-card-save-amount]')) {
    if (!showSave) continue;
    saveAmountEl.textContent = pill.dataset.saveText ? pill.dataset.saveText.replace(/^Spare\s*/, '') : '';
  }

  for (const energyBadge of card.querySelectorAll('[data-card-energy-badge]')) {
    energyBadge.hidden = !showPiSheet;
    if (showPiSheet) energyBadge.href = pill.dataset.piSheetUrl || '#';
  }

  for (const energyLetter of card.querySelectorAll('[data-card-energy-letter]')) {
    energyLetter.hidden = !energyClass;
    if (energyClass) {
      energyLetter.textContent = energyClass.toUpperCase();
      energyLetter.className = `energy-arrow card-energy-badge__arrow energy-arrow--${energyClass}`;
    }
  }

  for (const learnMoreButton of card.querySelectorAll('[data-card-learn-more]')) {
    learnMoreButton.href = pill.dataset.variantUrl || learnMoreButton.dataset.baseUrl;
  }

  const sizeValue = pill.textContent.trim();
  for (const titleWrapper of card.querySelectorAll('[data-card-title-wrapper]')) {
    const titleEl = titleWrapper.querySelector('h1, h2, h3, h4, h5, h6, p');
    if (!titleEl) continue;

    const baseTitle = titleWrapper.dataset.baseTitle || '';
    // Same pattern as the PDP's own title-variant sync (assets/product-title-variant.js):
    // split "<name> (<year>)" into a prefix and a "(YYYY)" suffix, then insert
    // the selected size between them.
    const match = baseTitle.match(/^(.*?)(\s*\(\d{4}\))\s*$/);
    const prefix = match ? match[1].trim() : baseTitle;
    const suffix = match ? match[2].trim() : '';

    titleEl.textContent = suffix ? `${prefix} ${sizeValue} ${suffix}` : `${prefix} ${sizeValue}`;
  }
});
